import { skipToken } from '@reduxjs/toolkit/query';
import Loader from 'components/Loader';
import { RECORD_BAND_CLASS, TAB_STRIP_BAND_CLASS } from 'components/record-header/RecordHeader';
import TabsGallery, { TabType } from 'components/TabsGallery';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import { FC, useMemo } from 'react';
import { useParams } from 'react-router';

import AuditInfo from 'components/audit/AuditInfo';
import { SectionEditProvider } from 'components/section-edit/SectionEdit';
import { SectionEditingResource } from 'components/section-edit/useSectionEditingSession';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import TasksView from 'pages/matters/views/TasksView';
import { useGetCorrespondenceByIdQuery } from 'store/api/correspondence';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useClassifiers } from 'utils/useClassifiers';
import CorrespondencePageHeader, { CORRESPONDENCE_UPDATE_PERMISSIONS } from './CorrespondencePageHeader';
import GeneralView from './views/GeneralView';

const USED_CLASSIFIERS = [
    'CORRESPONDENCE_TYPE',
    'CORRESPONDENCE_DIRECTION',
    'CORRESPONDENCE_STATUS_TYPE',
    'CORRESPONDENCE_CATEGORY_TYPE',
];

const CorrespondencePage: FC = () => {
    const { id: correspondenceId } = useParams();
    const isNew = useIsNewDesign();
    const canUpdate = useHasPermissions(CORRESPONDENCE_UPDATE_PERMISSIONS);

    const { data: correspondence, isLoading: isEntityLoading } = useGetCorrespondenceByIdQuery(
        correspondenceId ?? skipToken
    );

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: USED_CLASSIFIERS });

    const tabComponents = useMemo<TabType[]>(
        () =>
            correspondence
                ? [
                      {
                          id: 'general',
                          label: t('correspondence.general'),
                          component: (
                              <GeneralView correspondence={correspondence} classifiers={classifiers} isNew={isNew} />
                          ),
                      },
                      {
                          id: 'tasks',
                          label: t('correspondence.tasks'),
                          component: <TasksView correspondenceId={correspondenceId} />,
                      },
                  ]
                : [],
        [correspondence, classifiers, correspondenceId, isNew]
    );

    // A section in edit holds the correspondence's editing session, as the classic edit page does.
    const recordId = correspondence?.id;
    const editingSession = useMemo<SectionEditingResource | undefined>(
        () =>
            recordId === undefined
                ? undefined
                : {
                      resourceType: 'CORRESPONDENCE',
                      resourceId: recordId,
                      messageId: 'correspondence.concurrent-editing-warning',
                  },
        [recordId]
    );

    if (isEntityLoading || isLoadingClassifiers) {
        return <Loader />;
    }

    if (!correspondence) {
        return null;
    }

    const tabsGallery = (
        <TabsGallery
            tabs={tabComponents}
            tabGroupId="correspondence-page"
            trackTabsInHistoryState
            usePathBasedRouting
            panelClassName={isNew ? 'px-(--page-gutter-x) py-6' : undefined}
            // The new design's white band runs from the header through the tab strip, edge to edge.
            className={isNew ? TAB_STRIP_BAND_CLASS : undefined}
        />
    );
    const auditInfo = (
        <div className={cn('mt-6', isNew && 'mt-0 px-(--page-gutter-x) pb-(--page-gutter-y)')}>
            <AuditInfo
                createdAt={correspondence.createdAt}
                createdByEmployee={correspondence.createdByEmployee}
                lastModifiedAt={correspondence.lastModifiedAt}
                changedByEmployee={correspondence.changedByEmployee}
            />
        </div>
    );

    if (isNew) {
        return (
            <PaddedLayout className="new:p-0">
                <div className={RECORD_BAND_CLASS}>
                    <CorrespondencePageHeader correspondence={correspondence} classifiers={classifiers} isNew />
                </div>
                <SectionEditProvider canEdit={canUpdate} session={editingSession}>
                    {tabsGallery}
                </SectionEditProvider>
                {auditInfo}
            </PaddedLayout>
        );
    }

    return (
        <PaddedLayout>
            <CorrespondencePageHeader correspondence={correspondence} classifiers={classifiers} />
            {tabsGallery}
            {auditInfo}
        </PaddedLayout>
    );
};

export default CorrespondencePage;
